/**
 * 线上报错监控（PostHog）
 *
 * 用户那边出错、本地复现不了时，靠这里把现场带回来：报错堆栈、浏览器和设备、出错前的操作回放。
 * - 上报走同源 /api/ph，由 API 转给 PostHog（国内直连不稳，广告拦截也会挡）。
 * - 只在正式站点打开；本地要试就 localStorage.setItem('cyper.monitoring', '1') 后刷新。
 * - 回放遮住所有文字和输入（台词、素材名都是用户内容），视频、画布、预览框不录。
 * - SDK 动态加载，不拖慢首屏；加载完之前的报错不补报。
 */

import type { PostHog } from 'posthog-js';

/** PostHog 项目的公开写入 key，只能上报、不能读数据，放在前端代码里是官方做法。空着就整个关闭。 */
const POSTHOG_PROJECT_KEY = 'phc_naSzckdwsXhm5rCHA3qzJYiS5LLQXhs3233AYYtHp2NW';
const POSTHOG_UI_HOST = 'https://us.posthog.com';
const POSTHOG_PROXY_PATH = '/api/ph';
const LOCAL_OPT_IN_KEY = 'cyper.monitoring';
const LOCAL_HOSTNAME_RE = /^(localhost|127\.0\.0\.1|\[::1\])$|\.localhost$/;

let client: PostHog | null = null;
let started = false;
/** 登录状态可能比 SDK 先到，先记下来，加载完再补上。 */
let pendingUserId: string | null | undefined;
let identifiedUserId: string | null = null;
const watchedFrameWindows = new WeakSet<Window>();

export function isMonitoringEnabled(): boolean {
  if (!POSTHOG_PROJECT_KEY || typeof window === 'undefined') return false;
  if (!LOCAL_HOSTNAME_RE.test(window.location.hostname)) return true;
  try {
    return window.localStorage.getItem(LOCAL_OPT_IN_KEY) === '1';
  } catch {
    return false;
  }
}

export function startMonitoring(): void {
  if (started || !isMonitoringEnabled()) return;
  started = true;
  void import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(POSTHOG_PROJECT_KEY, {
        api_host: POSTHOG_PROXY_PATH,
        ui_host: POSTHOG_UI_HOST,
        defaults: '2026-08-30',
        person_profiles: 'identified_only',
        // 只为排查问题：报错 + 回放 + 页面切换，不做点击统计和问卷。
        autocapture: false,
        capture_pageview: 'history_change',
        capture_exceptions: true,
        disable_surveys: true,
        disable_product_tours: true,
        disable_web_experiments: true,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '*',
          blockSelector: 'video, canvas, iframe, hyperframes-player',
        },
      });
      posthog.register({ client: 'web' });
      client = posthog;
      if (pendingUserId !== undefined) applyUser(pendingUserId);
    })
    .catch(() => undefined);
}

/** 登录、退出、换账号时调用；只传站内用户 id，不传手机号。 */
export function setMonitoringUser(userId: string | null): void {
  pendingUserId = userId;
  if (client) applyUser(userId);
}

export function captureMonitoringException(
  error: unknown,
  properties?: Record<string, string | number | boolean | undefined>,
): void {
  try {
    client?.captureException(error, properties);
  } catch {
    // 监控本身出问题不能影响页面。
  }
}

/**
 * 预览框里的报错不会冒到外层页面，要单独挂监听。
 * 每次预览框加载完都可以调用；跨域的预览框读不到，直接跳过。
 */
export function watchPreviewFrameErrors(frame: HTMLIFrameElement): void {
  if (!started) return;
  let frameWindow: Window | null;
  try {
    frameWindow = frame.contentWindow;
    if (!frameWindow || watchedFrameWindows.has(frameWindow)) return;
    frameWindow.addEventListener('error', (event) => {
      captureMonitoringException(event.error ?? event.message, { source: 'preview-frame' });
    });
    frameWindow.addEventListener('unhandledrejection', (event) => {
      captureMonitoringException(event.reason, { source: 'preview-frame' });
    });
    watchedFrameWindows.add(frameWindow);
  } catch {
    // 跨域的预览框不允许访问。
  }
}

function applyUser(userId: string | null): void {
  if (!client || userId === identifiedUserId) return;
  // 换账号或退出：先断开上一个人，免得两个人的报错混在一起。
  if (identifiedUserId) client.reset();
  if (userId) client.identify(userId);
  identifiedUserId = userId;
}
